import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Form, Field, reduxForm, getFormSyncErrors } from 'redux-form';
import trans from 'counterpart';
import validate from '../../reservation/validate';
import { getForm, postFormPost } from '../../../api/formApi';
import TextField from './TextField';
import TextareaField from './TextareaField';
import RadioButtonsField from './RadioButtonsField';

const isRequired = value => (value || typeof value === 'number' ? undefined : trans('form.field_mandatory'));
const isValidEmail = value => value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
  ? trans('form.field_email_invalid')
  : undefined;

class FormBlock extends Component {
  state = {
    fields: {},
    message: null
  }

  componentDidMount = () => {
    if (this.isRemoteForm()) {
      getForm(this.props.block.form.id)
        .then(response => this.setState({ fields: response.data }));
    }
  }

  componentDidUpdate = (prevProps) => {
    if (!prevProps.invalid && this.props.invalid && this.props.form === 'customer' && this.props.triggerSubmit) {
      this.props.touch(this.props.form, ...Object.keys(this.props.formSyncErrors));
    }
  }

  getCSRFToken = () => this.state.fields._token && this.state.fields._token.value

  isRemoteForm = () => this.props.block.form && this.props.block.form.id;

  submitForm = (values) => {
    if (this.state.message) {
      this.setState({ message: null });
    }
    if (this.isRemoteForm()) {
      postFormPost(this.props.block.form.id, Object.keys(values).reduce((prev, cur) => ({
        ...prev,
        [cur]: {
          [Object.keys(this.state.fields[cur].properties)[0]]: values[cur]
        }
      }), {
        _token: this.getCSRFToken()
      }))
        .then(({ data: { message, confirmation_page: confirmationPage } }) => {
          if (confirmationPage) {
            this.props.history.push(confirmationPage);
          } else {
            this.setState({ message });
          }
        });
    }
  }

  renderField = (name) => {
    const field = this.state.fields[name];
    const required = typeof field.required !== 'undefined';

    if (name === '_token') return null;

    if (field.properties && field.properties.options) {
      let propertiesPath = field.properties.options;
      if (field.properties.options.items) propertiesPath = field.properties.options.items;
      return (
        <Field
          key={name}
          name={name}
          component={RadioButtonsField}
          label={field.title}
          validate={required ? isRequired : undefined}
          required={required}
          options={propertiesPath.enum.map((id, i) => ({
            key: id,
            value: propertiesPath.enum_titles[i]
          }))}
        />
      );
    }

    if (field.properties && field.properties.value) {
      switch (field.properties.value.widget) {
        case 'textarea': {
          return (
            <Field
              key={name}
              name={name}
              component={TextareaField}
              label={field.title}
              validate={required ? isRequired : undefined}
              required={required}
            />
          );
        }
        default: {
          const validators = [];
          if (required) validators.push(isRequired);
          if (field.properties.value.widget === 'email') validators.push(isValidEmail);
          return (
            <Field
              key={name}
              name={name}
              type={field.properties.value.widget}
              component={TextField}
              label={field.title}
              validate={validators}
              required={required}
            />
          );
        }
      }
    }

    return (
      <Field
        key={name}
        name={name}
        component={TextField}
        label={field.title}
        validate={required ? isRequired : undefined}
        required={required}
      />
    );
  }

  render = () => {
    const { pristine, placeholder, handleSubmit, submitting } = this.props;

    if (!this.isRemoteForm()) {
      return (<form onSubmit={handleSubmit}>{placeholder(0)}</form>);
    }

    return (
      <section className="form-holder p-t-sm p-b-lg">
        <div className="container">
          <Form
            onSubmit={handleSubmit(this.submitForm)}
            className="contact-form form-horizontal"
          >
            {Object.keys(this.state.fields).map(field => this.renderField(field))}
            <div className="form-group">
              <div className="col-md-12 pointer-events">
                <span className="req">
                  <bdi>*</bdi>
                  {trans('form.fields_required')}
                </span>
                <button
                  type="submit"
                  id="form-send"
                  className={`btn btn-send btn-lg btn-orange btn-submit ${pristine || submitting || this.state.message === 'Success' ? 'disabled' : ''}`}
                  disabled={pristine || submitting || this.state.message === 'Success'}
                >
                  {trans('button.submit')}
                </button>
              </div>
            </div>
          </Form>
          {this.state.message === 'Success' && (
            <div className="success-message-style">
              Bedankt! Wij hebben het formulier ontvangen.
            </div>
          )}
        </div>
      </section>
    );
  }
}

FormBlock.propTypes = {
  block: PropTypes.object,
  handleSubmit: PropTypes.func,
  history: PropTypes.object.isRequired,
  submitting: PropTypes.bool.isRequired,
  placeholder: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  touch: PropTypes.func,
  triggerSubmit: PropTypes.bool,
  formSyncErrors: PropTypes.object,
  invalid: PropTypes.bool,
  form: PropTypes.string
};

const mapStateToProps = (state, ownProps) => ({
  form: ownProps.name || 'default',
  formSyncErrors: getFormSyncErrors(ownProps.name || 'default')(state)
});

export default compose(
  connect(mapStateToProps),
  reduxForm({
    destroyOnUnmount: false,
    enableReinitialize: true,
    validate,
    onSubmit: () => {
      // This prop needs to be added to support remote validation by triggering redux-form's `submit` action.
      // TODO: Allow defining a custom onSubmit action from CMS.
    },
  })
)(withRouter(FormBlock));
